import { PermitSingle } from '@uniswap/permit2-sdk'
import { TypedDataField } from 'ethers/lib/ethers'
import { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useSigner } from 'uniswap/src/contexts/UniswapContext'
import { Permit } from 'uniswap/src/data/tradingApi/__generated__/index'
import { signTypedData } from 'uniswap/src/features/transactions/signing'
import { useAsyncData } from 'utilities/src/react/hooks'

// 🔧 全局签名状态管理，防止多个实例同时签名相同数据
const globalSigningState = new Map<string, { 
  isSigningInProgress: boolean; 
  signature?: string; 
  instanceId?: string;
  timestamp?: number;
}>()

function getGlobalSigningKey(permitData: Permit | null | undefined): string | null {
  if (!permitData) return null
  // 🔧 使用更稳定的键，避免因为对象序列化顺序不同导致的问题
  const domain = permitData.domain
  const values = permitData.values
  if (!domain || !values) return null
  
  return `${domain.name}-${domain.chainId}-${values.details?.nonce || values.nonce}-${values.details?.expiration || values.expiration}`
}

// 🔧 清理过期的全局状态
function cleanupExpiredGlobalState() {
  const now = Date.now()
  const FIVE_MINUTES = 5 * 60 * 1000
  
  for (const [key, state] of globalSigningState.entries()) {
    if (state.timestamp && now - state.timestamp > FIVE_MINUTES) {
      console.log(`🧹 清理过期的全局签名状态: ${key}`)
      globalSigningState.delete(key)
    }
  }
}

export type PermitSignatureInfo = {
  signature: string
  permitMessage: PermitSingle
  nonce: number
  expiry: number
}

// 🔧 从 signature 和 permitData 构建完整的 permit 对象
export function buildPermitFromSignature(
  signature: string, 
  permitData: Permit
): {
  permitSignature: string
  permitNonce: string
  permitExpiration: number
  permitAmount: string
  permitSigDeadline: number
  permitMessage?: PermitSingle
} | null {
  if (!signature || !permitData) {
    return null
  }

  // 🔍 从 permitData.values 中提取关键信息
  const values = permitData.values
  const details = values?.details
  
  // 构建 permit 对象
  const permit = {
    permitSignature: signature,
    permitNonce: (details?.nonce || values?.nonce || 0).toString(),
    permitExpiration: details?.expiration || values?.expiration || Math.floor(Date.now() / 1000) + 3600,
    permitAmount: details?.amount || "0",
    permitSigDeadline: values?.sigDeadline || Math.floor(Date.now() / 1000) + 3600,
  }

  // 🔧 可选：构建 PermitSingle 对象 (用于链上交互)
  if (details && permitData.domain) {
    const permitMessage: PermitSingle = {
      details: {
        token: details.token,
        amount: details.amount,
        expiration: details.expiration,
        nonce: details.nonce
      },
      spender: values?.spender || "",
      sigDeadline: values?.sigDeadline || Math.floor(Date.now() / 1000) + 3600
    }
    
    return {
      ...permit,
      permitMessage
    }
  }

  return permit
}

// Used to sign permit messages where we already have the domain, types, and values.
export function usePermit2SignatureWithData({ permitData, skip }: { permitData: Maybe<Permit>; skip?: boolean }): {
  isLoading: boolean
  signature: string | undefined
} {
  const signer = useSigner()
  const [isLoading, setIsLoading] = useState(false)
  const [signature, setSignature] = useState<string | undefined>(undefined)
  const [hasStartedSigning, setHasStartedSigning] = useState(false)
  
  // 🔧 为每个 hook 实例生成唯一标识
  const instanceId = useMemo(() => Math.random().toString(36).substr(2, 9), [])
  
  // 🚨 使用 ref 跟踪签名状态，防止组件重新挂载时的重复签名
  const signingRef = useRef<{
    isSigningInProgress: boolean
    globalKey: string | null
  }>({ isSigningInProgress: false, globalKey: null })
  
  // 🚨 检测重复 hook 调用
  useEffect(() => {
    console.log(`🚨 usePermit2SignatureWithData[${instanceId}] hook 初始化:`, {
      hasPermitData: !!permitData,
      permitDataDomain: permitData?.domain?.name,
      skip,
      timestamp: new Date().toISOString()
    })
    
    return () => {
      console.log(`🚨 usePermit2SignatureWithData[${instanceId}] hook 清理`)
    }
  }, [])

  const { domain, types, values } = permitData || {}
  
  // 🔧 为 permitData 生成唯一标识，用于检测是否是相同的签名请求
  const permitDataHash = useMemo(() => {
    if (!permitData) return null
    return JSON.stringify(permitData).substring(0, 50)
  }, [permitData])

  // 🔧 重写签名逻辑，正确处理 skip 参数和全局状态
  useEffect(() => {
    // 🧹 定期清理过期状态
    cleanupExpiredGlobalState()
    
    const globalKey = getGlobalSigningKey(permitData)
    const globalState = globalKey ? globalSigningState.get(globalKey) : null
    
    // 🔍 调试 skip 条件 - 使用固定的实例标识
    console.log(`🔍 usePermit2SignatureWithData[${instanceId}] effect 触发:`, {
      skip,
      hasSigner: !!signer,
      hasDomain: !!domain,
      hasTypes: !!types,
      hasValues: !!values,
      hasStartedSigning,
      currentSignature: signature?.substring(0, 20) + '...',
      globalKey,
      globalIsSigningInProgress: globalState?.isSigningInProgress || false,
      globalHasSignature: !!globalState?.signature,
      globalInstanceId: globalState?.instanceId,
      shouldSign: !skip && !!signer && !!domain && !!types && !!values && !hasStartedSigning && !signature && !globalState?.isSigningInProgress,
      // 🔧 添加 permitData 标识，检测是否是同一个签名请求
      permitDataHash
    })

    // 🔧 检查全局状态，如果已有签名，直接使用
    if (globalState?.signature && !signature) {
      console.log(`🔄 usePermit2SignatureWithData[${instanceId}]: 使用全局已有签名 (来自实例 ${globalState.instanceId})`)
      setSignature(globalState.signature)
      return
    }

    // 🚨 如果全局正在签名且不是当前实例，跳过
    if (globalState?.isSigningInProgress && globalState.instanceId !== instanceId) {
      console.log(`⏳ usePermit2SignatureWithData[${instanceId}]: 等待其他实例 ${globalState.instanceId} 完成签名`)
      return
    }

    // 🚨 如果全局正在签名且是当前实例，但本地已经开始签名，跳过（防止重复）
    if (globalState?.isSigningInProgress && globalState.instanceId === instanceId && hasStartedSigning) {
      console.log(`🔄 usePermit2SignatureWithData[${instanceId}]: 本实例已在签名中，跳过重复触发`)
      return
    }

    // 🚨 使用 ref 检查是否已经在签名中（防止快速重新挂载导致的重复）
    if (signingRef.current.isSigningInProgress && signingRef.current.globalKey === globalKey) {
      console.log(`🔄 usePermit2SignatureWithData[${instanceId}]: ref 检测到已在签名中，跳过`)
      return
    }

    // 如果应该跳过或已经有签名，不执行
    if (skip || !signer || !domain || !types || !values || hasStartedSigning || signature) {
      return
    }

    // 🔧 标记全局和本地签名状态
    if (globalKey) {
      globalSigningState.set(globalKey, { 
        isSigningInProgress: true, 
        instanceId,
        timestamp: Date.now()
      })
      // 🚨 同时更新 ref 状态
      signingRef.current = { isSigningInProgress: true, globalKey }
    }
    setHasStartedSigning(true)
    setIsLoading(true)

    console.log(`🔐 usePermit2SignatureWithData[${instanceId}]: 开始签名流程 (全局键: ${globalKey})`)

    const signPermit = async () => {
      try {
        const result = await signTypedData(
          domain,
          types as Record<string, TypedDataField[]>,
          values as Record<string, unknown>,
          signer,
        )

        console.log(`✅ usePermit2SignatureWithData[${instanceId}]: 签名成功:`, {
          signature: result?.substring(0, 20) + '...',
          signatureLength: result?.length,
          globalKey
        })

        // 🔧 保存到全局状态和本地状态
        if (globalKey && result) {
          globalSigningState.set(globalKey, { 
            isSigningInProgress: false, 
            signature: result,
            instanceId,
            timestamp: Date.now()
          })
          // 🚨 清理 ref 状态
          signingRef.current = { isSigningInProgress: false, globalKey: null }
          console.log(`💾 usePermit2SignatureWithData[${instanceId}]: 签名已保存到全局状态`)
        }
        setSignature(result)
      } catch (error) {
        console.error(`❌ usePermit2SignatureWithData[${instanceId}]: 签名失败:`, error)
        // 🔧 签名失败时重置全局和本地状态，允许重试
        if (globalKey) {
          globalSigningState.set(globalKey, { 
            isSigningInProgress: false,
            instanceId,
            timestamp: Date.now()
          })
          // 🚨 清理 ref 状态
          signingRef.current = { isSigningInProgress: false, globalKey: null }
        }
        setHasStartedSigning(false)
      } finally {
        setIsLoading(false)
      }
    }

    signPermit()
  }, [skip, signer, domain, types, values, hasStartedSigning, signature])

  // 🔧 当 skip 变为 true 时，重置状态（但保留已有签名）
  useEffect(() => {
    if (skip) {
      console.log(`🔄 usePermit2SignatureWithData[${instanceId}]: skip=true，重置状态`, {
        hadSignature: !!signature,
        signatureLength: signature?.length || 0,
        wasLoading: isLoading,
        hadStartedSigning: hasStartedSigning
      })
      setIsLoading(false)
      setHasStartedSigning(false)
      // 🔧 关键修复：保留已有的签名，不要重置
      // 这样其他实例就不会干扰已完成的签名
    }
  }, [skip, signature, isLoading, hasStartedSigning, instanceId])

  return {
    isLoading,
    signature,
  }
}
